import {
  Box,
  Typography,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ImgPath from "Constants/Imgs";
import ColorData from "Components/CstmTheme/ColorData";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../../Redux/User/Action";
import { UserInitialStateType } from "DataTypes/Redux";
import SubTopBar, { menuItems } from "./SubTopBar";
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from "react";
import MobileDrawer from "Components/commonComponents/HederDrawer";
import SidebarDrawer from "Components/commonComponents/sidebarDrawer";
import { handleNavigation } from "Components/Header";
import { navItems } from "./Sidebar";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// SignalR Service Imports
import { onReceiveGlobalSOS, offReceiveGlobalSOS, disconnectChatHub, initializeChatHub, onReceiveGlobalMessage } from "../../Service/signalRService"; 
import { RouteUrls } from "Constants/RouteUrls";

interface NotificationItem {
  id?: number;
  title?: string;
  message?: string;
  time?: string;
  caseId?: number;
  link?: string;
}

const Topbar = ({ onMenuClick }: { onMenuClick?: () => void }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);

  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openNotifications = Boolean(anchorEl);

  // 1. Create a persistent Audio object (prevents re-loading the file every time)
const [notificationSound] = useState(new Audio("/Assests/audio/notification-alert.mp3"));


const [notifications, setNotifications] = useState<NotificationItem[]>(() => {
  const saved = localStorage.getItem("unread_notifications");
  return saved ? JSON.parse(saved) : [];
});

// Function to sync state with LocalStorage
const syncNotifications = () => {
  const saved = localStorage.getItem("unread_notifications");
  setNotifications(saved ? JSON.parse(saved) : []);
};

useEffect(() => {
  // Listen for the custom event we created in ChatRoom
  window.addEventListener("notificationsUpdated", syncNotifications);
  
  // Also sync when the storage changes in another tab (optional but good)
  window.addEventListener("storage", syncNotifications);

  return () => {
    window.removeEventListener("notificationsUpdated", syncNotifications);
    window.removeEventListener("storage", syncNotifications);
  };
}, []);

// 2. Save to LocalStorage whenever notifications change
useEffect(() => {
  localStorage.setItem("unread_notifications", JSON.stringify(notifications));
}, [notifications]);

useEffect(() => {
  const token = localStorage.getItem("token");
  if (!token || !myInfo) return;

  initializeChatHub(token).then(() => {
    
    // --- LISTENER A: SOS ALERTS (Admins only) ---
    const isUserAdmin = myInfo?.userType === "ADMIN" || myInfo?.userType === "Admin";
    if (isUserAdmin) {
      onReceiveGlobalSOS((data: any) => {
        addNotification("🚨 SOS URGENT", `Case #${data.caseId} needs help!`, data.caseId);
        playNotificationSound(); // Trigger Sound
      });
    }

    // --- LISTENER B: NEW MESSAGES (Everyone) ---
    onReceiveGlobalMessage((data: any) => {
      // Don't show notification if user is already looking at this chat
      const currentPath = window.location.pathname;
      if (currentPath.includes(`/Chat/${data.caseId}`)) return;

      addNotification("💬 New Message", `Case #${data.caseId}: ${data.content}`, data.caseId);
      playNotificationSound(); // Trigger Sound
    });
  });

  return () => {
    offReceiveGlobalSOS();
    // connection.off("ReceiveMessageGlobal"); // Add this cleanup to service
  };
}, [myInfo]);

  // 2. Helper function to play sound with error handling
  const playNotificationSound = () => {
    notificationSound.currentTime = 0;
    notificationSound.play().catch((error) => {
      console.warn("Audio play blocked by browser. User must interact with the page first.", error);
    });
  };

// Helper to add notification without duplication
const addNotification = (title: string, message: string, caseId: number) => {
  const newNotify: NotificationItem = {
    id: Date.now(),
    title,
    message,
    time: dayjs().format('h:mm A'),
    caseId: caseId,
    link: `/chat?openCase=${caseId}`
  };

  // setNotifications(prev => [newNotify, ...prev]);
  setNotifications(prev => {
    const updated = [newNotify, ...prev];
    localStorage.setItem("unread_notifications", JSON.stringify(updated));
    return updated;
  });
  toast.info(message);
};

// 3. Clear single notification when clicked
const handleItemClick = (caseId?: number, noteId?: number) => {
  if (noteId) {
    setNotifications(prev => prev.filter(n => n.id !== noteId));
  }
  handleNotificationClose();
  
  if (caseId) {
    navigate(RouteUrls.chat, { state: { openCaseId: caseId } });
  }
};

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    await disconnectChatHub();
    localStorage.clear();
    dispatch(clearUser() as any);
    window.location.href = "/home.html";
  };
  const currentDateTime = dayjs().format('D MMM, YYYY / h:mmA');
  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    // Reset expanded items when drawer closes for better UX
    setExpandedItems({});
  }, []);
  const handleExpandClick = (id: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };


  return (
    <Box
      sx={{
        bgcolor: "white",
        boxShadow: 2,
        position: "sticky",
        top: 0,
        zIndex: 1000,
        minHeight: myInfo?.userType === "USER" ? "140px" : "80px", // Dynamic height based on user type
      }}
      className="topbar-box"
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          minHeight: "80px", // Fixed height for main header content
        }}
      >
        {/* Left Section - Logo + Welcome */}
        <Box display="flex" alignItems="center" gap={2}>
          {isMobile && (
            <IconButton onClick={() => setSidebarDrawerOpen(true)} sx={{ color: ColorData.text.primary }}>
              <MenuIcon />
            </IconButton>
          )}
          <Avatar src={myInfo?.profileImageUrl || "/assets/images/dashboard-icons/profile-img.png"} />
          <Typography>
            Welcome,{" "}
            <strong style={{ color: ColorData.heading.main }}>
              {myInfo?.firstname || "Admin"}
            </strong>
          </Typography>
        </Box>

        {/* Right Section */}
        <Box display="flex" alignItems="center" gap={1}>
          
          {/* NOTIFICATION BELL */}
          <IconButton onClick={handleNotificationClick} sx={{ color: ColorData.text.primary }}>
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={openNotifications}
            onClose={handleNotificationClose}
            PaperProps={{ sx: { width: 320, maxHeight: 400, mt: 1.5, boxShadow: 3 } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Typography sx={{ p: 2, fontWeight: 'bold' }}>Notifications</Typography>
            <Divider />
            {notifications.length > 0 ? (
              notifications.map((note) => (
                <MenuItem 
                  key={note.id} 
                  onClick={() => handleItemClick(note.caseId, note.id)} 
                  sx={{ 
                    py: 1.5, 
                    backgroundColor: note?.title?.includes("SOS") ? "#fff5f5" : "inherit" 
                  }}
                >
                  <ListItemText 
                    primary={note?.title || ""} 
                    secondary={
                      <>
                        <Typography variant="caption" display="block" color="text.primary">
                          {note.message}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ fontSize: '10px' }}>
                          {note.time}
                        </Typography>
                      </>
                    } 
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem onClick={handleNotificationClose}>
                <Typography variant="body2">No new notifications</Typography>
              </MenuItem>
            )}
            <Divider />
            {/* <MenuItem sx={{ justifyContent: 'center' }}>
              <Typography variant="caption" color="primary" fontWeight="bold">
                View All
              </Typography>
            </MenuItem> */}
          </Menu>

          {!isMobile && (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography fontWeight={500} fontSize={"15px"}>
                {currentDateTime}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                  onClick={handleLogout}
                >
                  <ImgPath.LogoutIcon />
                  <Typography sx={{ ml: 1 }}>Logout</Typography>
                </IconButton>
              </Box>
            </Box>
          )}

          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: ColorData.text.primary }}>
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {myInfo?.userType === "USER" && (
        <Box sx={{ borderTop: "1px solid #e0e0e0", bgcolor: "white" }}>
          <SubTopBar />
        </Box>
      )}

      <MobileDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        navItems={menuItems}
        expandedItems={expandedItems}
        onExpandClick={handleExpandClick}
        onNavigate={(item: string) => {
          handleNavigation(item);
          handleDrawerClose();
        }}
      />

      <SidebarDrawer 
        open={sidebarDrawerOpen} 
        onClose={() => setSidebarDrawerOpen(false)} 
        navItems={navItems} 
        handleLogout={handleLogout} 
      />
    </Box>
  );
};

export default Topbar;
